import { Check, Copy } from "lucide-react";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgDialog,
  IgIconButton,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  ExperiencePageTargetingDto,
  GenericExperienceFe,
  OrganizationDto,
  RedirectDto,
} from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { getDialog, isDialogOpen, setDialog } from "@src/store/slices/global";
import { copyToClipboard } from "@src/utils/copyToClipboard";
import { generatePreviewLink } from "@src/utils/generatePreviewLink";

interface PreviewExperimentProps {
  experience: GenericExperienceFe;
  organization: OrganizationDto;
  redirects: RedirectDto[];
  pageTargeting: ExperiencePageTargetingDto[];
}
export const PreviewExperience = ({
  experience,
  organization,
  redirects,
  pageTargeting,
}: PreviewExperimentProps) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen("previewExperiment"));
  const dialog = useSelector(getDialog("previewExperiment"));
  const theme = useTheme();
  const handleClose = () => {
    dispatch(setDialog({ dialog: "previewExperiment", open: false }));
  };
  const previewLink = useMemo(() => {
    return generatePreviewLink({
      organization,
      experience,
      previewThemeId: dialog.props?.themeId,
      redirects,
      pageTargeting,
      useOriginUrl: true,
    });
  }, [organization, experience, dialog.props?.themeId]);

  const [copyClicked, setCopyClicked] = useImmer(false);

  const onPreviewLinkCopy = async () => {
    await copyToClipboard(previewLink);
    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };

  return (
    <IgDialog
      title={
        <Grid
          container
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          textAlign={"center"}
        >
          <Grid
            container
            direction={"row"}
            item
            justifyContent={"center"}
            alignItems={"center"}
            textAlign={"center"}
          >
            <IgTypography variant={"h2"}>
              Open Preview
              <IgTooltip
                title={
                  copyClicked ? "Copied Preview Link" : "Copy Preview Link"
                }
              >
                <IgIconButton
                  onClick={() => {
                    void onPreviewLinkCopy();
                  }}
                >
                  {copyClicked ? (
                    <Check size={20} color={theme.igIconColors.success} />
                  ) : (
                    <Copy size={20} />
                  )}
                </IgIconButton>
              </IgTooltip>
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography igStyle={"semibold"}>{experience.name}</IgTypography>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      showDialog={isOpen}
      dialogHeight={"fit-content"}
      noButtonText={"Get QR code for mobile"}
      yesButtonText={"Open desktop preview"}
      handleYesClick={() => {
        window.open(previewLink, "_blank");
      }}
      handleNoClick={() => {
        handleClose();
        dispatch(
          setDialog({
            dialog: "previewTestGroupCode",
            open: true,
            props: {
              id: dialog.props?.id,
              themeId: dialog.props?.themeId,
              path: dialog.props?.path,
            },
          })
        );
      }}
    />
  );
};
